import {
  HbrBanner,
  HbrDomTable,
  HbrIcon,
  HbrTab,
  HbrTabGroup,
  HbrTabPanel,
  HbrTag,
} from '@harbor/elements-react-standalone';
import { alertsInfoLarge } from '@harbor/elements/icons/index.js';
import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query'; //-tech-debt-react-query;
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ASSIGNMENT_LEVEL,
  ProductSupportPath,
  ProductSupportPathQueue,
  useMyProductSupportPathQueues,
} from '../../../hooks/useProductSupportPaths';
import { PHASE_COLOURS } from '../../../support/config/tables';
import { fetchItems, postItem, putItem } from '../../../support/queries';
import { queryClient } from '../../../support/queryClient';
import { RootState } from '../../../support/store';
import {
  formatSEPhase,
  getScreenSize,
  onScreenSizeChange,
  ScreenSize,
} from '../../../support/utils';
import {
  ApprovalRequestStatusLevel,
  EscalationLevel,
  EscalationRequest,
} from '../../../types/approval-request';
import { CacheKey } from '../../../types/cache-keys';
import { ColumnType } from '../../../types/table';
import { TemporaryUserView } from '../../../types/user';
import cellTemplate from '../../Common/TableView/cell-templates';
import StbButton from '../../security-toolbox-ui/StbButton';
import StbModal from '../../security-toolbox-ui/StbModal';
import BuEscalationModal from '../Escalation/BuEscalationModal';
import IncubationAssignmentModal from './IncubationAssignmentModal';

const QUEUE_STALE_TIME = 5 * 60 * 1000;
const QUEUE_CACHE_TIME = 30 * 60 * 1000;
const OPPORTUNITY_STALE_TIME = 10 * 60 * 1000;
const OPPORTUNITY_CACHE_TIME = 60 * 60 * 1000;

const NAVIGATION_WIDTH = 202;
const NAVIGATION_WIDTH_MINIMIZED = 72;
const PAGE_HORIZONTAL_PADDING = 32;
const RIGHT_EDGE_BUFFER = 10;
const WIDTH_SAFETY_BUFFER = 2;

const supportPathTableView = {
  columns: [
    {
      id: 'Name',
      name: 'Opportunity',
      columnType: ColumnType.TitleLink,
    },
    {
      id: 'product',
      name: 'Product',
      columnType: ColumnType.String,
    },
    {
      id: 'Amount',
      name: 'Amount(TCV)',
      columnType: ColumnType.Currency,
    },
    {
      id: 'StageName',
      name: 'Stage',
      columnType: ColumnType.Custom,
      renderCell: ({ data }) => {
        const parts = data.StageName?.split('-');
        const stage0 = parts?.[0]?.trim();
        const stage1 = parts?.[1]?.trim();

        if (!stage0) {
          return data.StageName || '';
        }

        const colorClass = PHASE_COLOURS[stage0] || 'bg-gray-400';

        return (
          <>
            <div
              className={`flex items-center justify-center min-w-7 min-h-7 rounded-full ${colorClass} mr-2 flex-0 font-bold text-lg text-white -my-1`}
            >
              {stage1 ? stage0 : ''}
            </div>
            <div className="flex-1 whitespace-nowrap">
              {stage1 ? stage1 : stage0}
            </div>
          </>
        );
      },
    },
    {
      id: 'CloseDate',
      name: 'Expected Close Date',
      columnType: ColumnType.Date,
    },
    {
      id: 'LastModifiedDate',
      name: 'Last Modified',
      columnType: ColumnType.Date,
    },
    {
      id: 'se_phase',
      name: 'SE Phase',
      columnType: ColumnType.String,
    },
  ],
  id: 'support-path-opportunities-view',
  filters: [],
  orderBy: { column: 'Name', order: 'asc' },
  userOwnedOnly: false,
} as TemporaryUserView;

const AssignedToCell = ({ data }) => {
  return <span>{data.assignedToEmail || '-'}</span>;
};

const assignedToColumn = {
  id: 'assignedToEmail',
  name: 'Assigned To',
  columnType: ColumnType.Custom,
  renderCell: AssignedToCell,
};

const TableLoadingSkeleton = ({
                                columnCount = 7,
                                rowCount = 5,
                                width,
                              }: {
  columnCount?: number;
  rowCount?: number;
  width?: number;
}) => {
  const gridTemplateColumns = useMemo(() => {
    if (columnCount <= 1) return 'minmax(0, 1fr)';

    return Array.from({ length: columnCount })
      .map((_, index) => {
        if (index === 0) return 'minmax(260px, 2fr)';
        if (index === 1) return 'minmax(180px, 1.2fr)';
        if (index === columnCount - 1) return 'minmax(160px, 1fr)';
        return 'minmax(140px, 1fr)';
      })
      .join(' ');
  }, [columnCount]);

  const shellStyle: React.CSSProperties = {
    width: width ? `${width}px` : '100%',
    maxWidth: width ? `${width}px` : '100%',
    minWidth: width ? `${width}px` : 0,
    boxSizing: 'border-box',
  };

  const rowStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns,
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
  };

  const cellStyle: React.CSSProperties = {
    width: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    overflow: 'hidden',
  };

  const barStyle: React.CSSProperties = {
    display: 'block',
    width: '100%',
    maxWidth: 'none',
  };

  return (
    <div
      className="support-path-table-loading"
      role="status"
      aria-label="loading team queue data"
      style={shellStyle}
    >
      <div className="hbr-css__table-loading" style={shellStyle}>
        <div className="hbr-css__table-loading-header" style={shellStyle}>
          <div className="hbr-css__table-loading-row" style={rowStyle}>
            {Array.from({ length: columnCount }).map((_, columnIndex) => (
              <div
                key={columnIndex}
                className="hbr-css__table-loading-cell"
                style={cellStyle}
              >
                <span
                  className="hbr-css__skeleton-strong hbr-css__skeleton-sm support-path-table-loading__bar"
                  style={barStyle}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="hbr-css__table-loading-body" style={shellStyle}>
          {Array.from({ length: rowCount }).map((_, rowIndex) => (
            <div
              key={rowIndex}
              className="hbr-css__table-loading-row"
              style={rowStyle}
            >
              {Array.from({ length: columnCount }).map((_, columnIndex) => (
                <div
                  key={columnIndex}
                  className="hbr-css__table-loading-cell"
                  style={cellStyle}
                >
                  <span
                    className="hbr-css__skeleton hbr-css__skeleton-sm support-path-table-loading__bar"
                    style={barStyle}
                  />
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const EmptyTableState = ({
                           message = 'No opportunities to show',
                         }: {
  message?: string;
}) => (
  <div className="p-6">
    <HbrBanner sentiment="info" variant="fill" open closable={false}>
      <HbrIcon slot="icon" asset={alertsInfoLarge} />
      {message}
    </HbrBanner>
  </div>
);

const HeaderLoadingSkeleton = ({
                                 width,
                               }: {
  width: number;
}) => (
  <div
    className="mb-4"
    style={{
      width,
      maxWidth: width,
      minWidth: width,
      boxSizing: 'border-box',
    }}
  >
    <div className="flex items-center gap-3 mb-2">
      <span
        className="hbr-css__skeleton-strong hbr-css__skeleton-md"
        style={{ display: 'inline-block', width: 360, maxWidth: '60%' }}
      />
    </div>
    <div className="flex flex-wrap gap-2 justify-end">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="hbr-css__skeleton hbr-css__skeleton-sm"
          style={{ display: 'inline-block', width: 120 }}
        />
      ))}
    </div>
  </div>
);

const ProductTags = ({
                       productNames,
                     }: {
  productNames: string[];
}) => {
  if (!productNames?.length) return null;

  return (
    <div
      className="flex flex-wrap gap-2 justify-end"
      style={{
        marginLeft: 'auto',
        maxWidth: '50%',
        minWidth: 0,
      }}
    >
      {productNames.map((productName) => (
        <HbrTag
          key={productName}
          size="small"
          sentiment="info"
          status={true}
        >
          {productName}
        </HbrTag>
      ))}
    </div>
  );
};

const useFetchEscalations = (params: any = {}, options: any = {}) => {
  return useQuery<any[]>({
    queryKey: [CacheKey.Escalations, 'queue', params.queueKey ?? 'unknown', params.stage ?? 'all'],
    queryFn: () => {
      const queryParams = {
        detailed: true,
        depth: 2,
        ...(params.stage ? { stage: params.stage } : {}),
      };

      return fetchItems(
        'db/escalation',
        queryParams,
        options
      );
    },
    ...{
        staleTime: QUEUE_STALE_TIME,
        gcTime: QUEUE_CACHE_TIME,
        placeholderData: undefined,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        enabled: !!params.queueKey && (options?.enabled ?? true),
      },
  });
};

const useFetchOpportunitiesById = (oppIds: string[]) => {
  const stableOppIds = useMemo(
    () => Array.from(new Set((oppIds ?? []).filter(Boolean).map(String))).sort(),
    [oppIds]
  );

  const sfColumns = useMemo(() => {
    return Array.from(
      new Set([
        'Name',
        'Amount',
        'StageName',
        'CloseDate',
        'LastModifiedDate',
        'se_phase',
      ])
    );
  }, []);

  const body = useMemo(
    () => ({
      filters: [
        {
          prop: 'Opportunity_ID_18__c',
          operator: 'anyOf',
          comparison: stableOppIds,
        },
      ],
      columns: sfColumns,
      search_string: '',
    }),
    [stableOppIds, sfColumns]
  );

  return useQuery<any[]>({
    queryKey: [CacheKey.Opportunity, 'support-path-queue', stableOppIds.join(',')],
    queryFn: () => fetchItems('sf/opportunity', {}, { body }),
    enabled: stableOppIds.length > 0,
    staleTime: OPPORTUNITY_STALE_TIME,
    gcTime: OPPORTUNITY_CACHE_TIME,

    /*
      Important:
      Do not keep prior team's opportunity enrichment data during transitions.
    */
    placeholderData: undefined,

    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 1,
  });
};

const normalizeId = (value: any) => String(value ?? '');

const getAssignmentLevel = (assignment: any) => {
  if (!assignment) return null;

  if (typeof assignment.assignment_type === 'object') {
    return assignment.assignment_type?.level ?? null;
  }

  if (assignment.assignment_type === 'Domain Specialist') {
    return ASSIGNMENT_LEVEL.DomainSpecialist;
  }

  if (assignment.assignment_type === 'Incubation Team') {
    return ASSIGNMENT_LEVEL.IncubationTeam;
  }

  return assignment?.product_support?.assignment_type_level ?? null;
};

const getAssignmentCreatedTime = (assignment: any) => {
  const raw = assignment?.created || assignment?.last_updated;
  const parsed = raw ? new Date(raw).getTime() : 0;
  return Number.isFinite(parsed) ? parsed : 0;
};

const getAssignmentAssignedToEmail = (assignment: any) => {
  return assignment?.assigned_to?.email || '';
};

const getLatestRelevantAssignment = (
  escalation: any,
  queue?: ProductSupportPathQueue
) => {
  const assignments = escalation?.assignments ?? [];
  if (!queue || !assignments.length) return null;

  const queueSupportPathIds = new Set(
    (queue.supportPathIds ?? []).map((id) => Number(id))
  );

  const relevant = assignments.filter((assignment) => {
    const productSupportId = assignment?.product_support?.id;
    const assignmentLevel = getAssignmentLevel(assignment);

    if (productSupportId && queueSupportPathIds.has(Number(productSupportId))) {
      return true;
    }

    return Number(assignmentLevel) === Number(queue.assignment_type_level);
  });

  if (!relevant.length) return null;

  return [...relevant].sort(
    (a, b) => getAssignmentCreatedTime(b) - getAssignmentCreatedTime(a)
  )[0];
};

const getEscalationSupportPathForQueue = (
  escalation: any,
  queue?: ProductSupportPathQueue
): ProductSupportPath | undefined => {
  if (!escalation || !queue) return undefined;

  const queueSupportPathIds = new Set(queue.supportPathIds.map((id) => Number(id)));

  const availablePath = escalation.available_support_paths?.find((path) =>
    queueSupportPathIds.has(Number(path.id))
  );

  if (availablePath) {
    const fullQueuePath = queue.supportPaths.find(
      (path) => Number(path.id) === Number(availablePath.id)
    );

    return {
      ...(fullQueuePath ?? {}),
      ...availablePath,
      group_members: queue.group_members,
    } as ProductSupportPath;
  }

  const assignment = getLatestRelevantAssignment(escalation, queue);

  if (assignment?.product_support) {
    return {
      ...assignment.product_support,
      id: assignment.product_support.id,
      group_members: queue.group_members,
    } as ProductSupportPath;
  }

  return undefined;
};

const getOpportunityId = (escalation: any) => {
  return escalation?.opportunity?.id || escalation?.opportunity || '';
};

const getOpportunityFallbackName = (escalation: any) => {
  return (
    escalation?.opportunity_name ||
    getOpportunityId(escalation) ||
    `Escalation ${escalation?.id}`
  );
};

const mapData = (
  escalations: any[],
  opportunities: any[],
  queue?: ProductSupportPathQueue,
) => {
  return (escalations ?? []).map((escalation) => {
    const oppId = getOpportunityId(escalation);

    const sfOpportunity = (opportunities ?? []).find((opportunity) => {
      return (
        opportunity?.Id === oppId ||
        opportunity?.Opportunity_ID_18__c === oppId ||
        opportunity?.id === oppId
      );
    });

    const latestAssignment = getLatestRelevantAssignment(escalation, queue);

    const rowData = supportPathTableView.columns.reduce((acc, column) => {
      acc[column.id] =
        sfOpportunity?.[column.id]?.id ??
        sfOpportunity?.[column.id] ??
        null;
      return acc;
    }, {} as any);

    return {
      ...rowData,
      id: escalation.id,
      Name: rowData.Name || getOpportunityFallbackName(escalation),
      product:
        escalation.product?.name ||
        escalation.product_name ||
        sfOpportunity?.product ||
        null,
      Amount: rowData.Amount ?? sfOpportunity?.Amount ?? null,
      StageName: rowData.StageName ?? sfOpportunity?.StageName ?? '',
      CloseDate: rowData.CloseDate ?? sfOpportunity?.CloseDate ?? null,
      LastModifiedDate:
        rowData.LastModifiedDate ??
        sfOpportunity?.LastModifiedDate ??
        escalation.last_updated ??
        null,
      se_phase: formatSEPhase(
        rowData.se_phase ??
        sfOpportunity?.se_phase ??
        escalation.opportunity?.se_phase ??
        ''
      ),
      assignedToEmail: getAssignmentAssignedToEmail(latestAssignment),
      escalation: escalation.id,
      titleLink: oppId ? `/opportunity/${oppId}` : undefined,
    };
  });
};

const IncubationTeamOpportunityList = () => {
  const { supportPathId: queueKey } = useParams();
  const navigate = useNavigate();

  const menuMinimized = useSelector((state: RootState) => state.ui.minimizeMenu);

  const [screenSize, setScreenSize] = useState<ScreenSize>(() => getScreenSize());
  const [currentTab, setCurrentTab] = useState('unassigned');
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [unassignModalOpen, setUnassignModalOpen] = useState(false);
  const [selectedEscalation, setSelectedEscalation] = useState<string | number>();

  const [escalateModalOpen, setEscalateModalOpen] = useState<boolean>(false);

  useEffect(() => {
    return onScreenSizeChange(setScreenSize);
  }, []);

  const tableWidth = useMemo(() => {
    const activeNavWidth = menuMinimized
      ? NAVIGATION_WIDTH_MINIMIZED
      : NAVIGATION_WIDTH;

    return Math.max(
      320,
      screenSize.width -
      activeNavWidth -
      PAGE_HORIZONTAL_PADDING -
      RIGHT_EDGE_BUFFER -
      WIDTH_SAFETY_BUFFER
    );
  }, [screenSize.width, menuMinimized]);

  const pageWidth = useMemo(() => {
    return tableWidth + PAGE_HORIZONTAL_PADDING;
  }, [tableWidth]);

  const fullWidthShellStyle = useMemo<React.CSSProperties>(() => ({
    width: `${tableWidth}px`,
    maxWidth: `${tableWidth}px`,
    minWidth: `${tableWidth}px`,
    boxSizing: 'border-box',
  }), [tableWidth]);

  const pageShellStyle = useMemo<React.CSSProperties>(() => ({
    width: `${pageWidth}px`,
    maxWidth: `${pageWidth}px`,
    minWidth: 0,
    boxSizing: 'border-box',
  }), [pageWidth]);

  const isAdmin = useSelector((state: RootState) => state.user?.authz?.isAdmin);
  const user = useSelector((state: RootState) => state.user?.userData);

  const {
    data: myQueues = [],
    isLoading: queuesLoading,
  } = useMyProductSupportPathQueues();

  const selectedQueue = useMemo(() => {
    if (!queueKey) return undefined;
    return myQueues.find((queue) => queue.queueKey === queueKey);
  }, [myQueues, queueKey]);

  const assignmentLevel = selectedQueue?.assignment_type_level;

  const queueIdentity = useMemo(() => {
    return `${selectedQueue?.queueKey ?? 'none'}:${assignmentLevel ?? 'none'}`;
  }, [selectedQueue?.queueKey, assignmentLevel]);

  const isIncubationTeam =
    assignmentLevel === ASSIGNMENT_LEVEL.IncubationTeam;

  const isDomainSpecialist =
    assignmentLevel === ASSIGNMENT_LEVEL.DomainSpecialist;

  useEffect(() => {
    if (queuesLoading) return;
    if (queueKey) return;

    const firstQueue = myQueues[0];

    if (firstQueue) {
      navigate(`/opportunity-team/${firstQueue.queueKey}`, { replace: true });
    }
  }, [queuesLoading, queueKey, myQueues, navigate]);

  const escalationFetchStage = isDomainSpecialist
    ? undefined
    : assignmentLevel;

  const {
    data: escalations = [],
    isLoading: escalationsLoading,
    isFetching: escalationsFetching,
  } = useFetchEscalations(
    {
      queueKey: selectedQueue?.queueKey,
      stage: escalationFetchStage,
    },
    {
      enabled:
        !!selectedQueue?.queueKey &&
        (isDomainSpecialist || !!assignmentLevel),
    }
  );

  const myTeamEscalations = useMemo(() => {
    if (!selectedQueue || !assignmentLevel || !escalations) {
      return [];
    }

    const queueSupportPathIds = new Set(
      selectedQueue.supportPathIds.map((id) => Number(id))
    );

    return escalations.filter((escalation) => {
      const isCancelled =
        escalation?.status?.title === 'Cancelled' ||
        escalation?.status_name === 'Cancelled';

      if (isCancelled) return false;

      /*
        Domain Specialist queues:
        Fetch all stages, but only show assignments for the logged-in user.
        This catches Domain assignments even after the escalation has moved to
        Incubation, but does not show other engineers' assignments.
      */
      if (isDomainSpecialist) {
        if (!user?.email) return false;

        return (escalation.assignments ?? []).some((assignment) => {
          const assignmentLevel = getAssignmentLevel(assignment);
          const productSupportId = assignment?.product_support?.id;
          const assignedToEmail = assignment?.assigned_to?.email;

          return (
            Number(assignmentLevel) === ASSIGNMENT_LEVEL.DomainSpecialist &&
            productSupportId &&
            queueSupportPathIds.has(Number(productSupportId)) &&
            assignedToEmail === user.email
          );
        });
      }

      /*
        Incubation keeps the existing team-queue behavior:
        show escalations whose available support path belongs to this incubation
        queue.
      */
      return escalation.available_support_paths?.some(
        (path) =>
          queueSupportPathIds.has(Number(path.id)) &&
          Number(path.assignment_type_level) === Number(assignmentLevel)
      );
    });
  }, [
    selectedQueue,
    assignmentLevel,
    escalations,
    isDomainSpecialist,
    user?.email,
  ]);

  const oppIds = useMemo(() => {
    return Array.from(
      new Set(
        myTeamEscalations
          .map((escalation) => getOpportunityId(escalation))
          .filter(Boolean)
          .map(String)
      )
    ).sort();
  }, [myTeamEscalations]);

  const {
    data: opportunityData = [],
    isLoading: opportunitiesLoading,
    isFetching: opportunitiesFetching,
  } = useFetchOpportunitiesById(oppIds);

  const isInitialLoading =
    queuesLoading ||
    !selectedQueue ||
    escalationsLoading ||
    (oppIds.length > 0 && opportunitiesLoading);

  const isSoftFetching =
    escalationsFetching ||
    opportunitiesFetching;

  const assignedToMe = useMemo(() => {
    if (!myTeamEscalations || !user || !selectedQueue) {
      return [];
    }

    return myTeamEscalations.filter((escalation) => {
      const assignment = getLatestRelevantAssignment(escalation, selectedQueue);

      return (
        assignment?.assigned_to &&
        assignment.assigned_to.email === user.email
      );
    });
  }, [myTeamEscalations, user, selectedQueue]);

  const assignedToOthers = useMemo(() => {
    if (!myTeamEscalations || !user || !selectedQueue) {
      return [];
    }

    return myTeamEscalations.filter((escalation) => {
      const assignment = getLatestRelevantAssignment(escalation, selectedQueue);

      return (
        assignment?.assigned_to &&
        assignment.assigned_to.email !== user.email
      );
    });
  }, [myTeamEscalations, user, selectedQueue]);

  const unassigned = useMemo(() => {
    if (!myTeamEscalations || !selectedQueue) {
      return [];
    }

    return myTeamEscalations.filter((escalation) => {
      const assignment = getLatestRelevantAssignment(escalation, selectedQueue);
      return !assignment || !assignment.assigned_to;
    });
  }, [myTeamEscalations, selectedQueue]);

  const domainSpecialistAssigned = useMemo(() => {
    if (!myTeamEscalations || !selectedQueue || !user?.email) {
      return [];
    }

    const queueSupportPathIds = new Set(
      selectedQueue.supportPathIds.map((id) => Number(id))
    );

    return myTeamEscalations.filter((escalation) => {
      /*
        Use the latest relevant assignment so historical assignments do not cause
        the row to appear for the wrong user.
      */
      const assignment = getLatestRelevantAssignment(escalation, selectedQueue);

      if (!assignment) return false;

      const assignmentLevel = getAssignmentLevel(assignment);
      const productSupportId = assignment?.product_support?.id;
      const assignedToEmail = assignment?.assigned_to?.email;

      return (
        Number(assignmentLevel) === ASSIGNMENT_LEVEL.DomainSpecialist &&
        productSupportId &&
        queueSupportPathIds.has(Number(productSupportId)) &&
        assignedToEmail === user.email
      );
    });
  }, [myTeamEscalations, selectedQueue, user?.email]);

  const selectedEscalationObject = useMemo(() => {
    return (myTeamEscalations ?? []).find(
      (escalation) => normalizeId(escalation.id) === normalizeId(selectedEscalation)
    );
  }, [myTeamEscalations, selectedEscalation]);

  const selectedEscalationAssignment = useMemo(() => {
    return getLatestRelevantAssignment(selectedEscalationObject, selectedQueue);
  }, [selectedEscalationObject, selectedQueue]);

  const selectedEscalationSupportPath = useMemo(() => {
    return getEscalationSupportPathForQueue(selectedEscalationObject, selectedQueue);
  }, [selectedEscalationObject, selectedQueue]);

  const invalidateQueueData = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: [CacheKey.Escalations], refetchType: 'all' }),
      queryClient.invalidateQueries({ queryKey: [CacheKey.ApprovalRequest], refetchType: 'all' }),
    ]);
  };

  const handleTabClick = (tabKey) => {
    setCurrentTab(tabKey);
  };

  const updateOrCreateAssignment = async ({
                                            escalation,
                                            assignment,
                                            supportPath,
                                            assignedToId,
                                            statusId,
                                            notes,
                                          }: {
    escalation: any;
    assignment?: any;
    supportPath: ProductSupportPath;
    assignedToId?: number | null;
    statusId: number | string;
    notes?: string | null;
  }) => {
    if (!user || !escalation || !supportPath) return;

    const assignmentPayload = {
      id: assignment?.id,
      escalation_id: escalation.id,
      assignment_type_id:
        supportPath.assignment_type_level === ASSIGNMENT_LEVEL.DomainSpecialist
          ? EscalationLevel.Domain
          : EscalationLevel.Incubation,
      status_id: Number(statusId),
      product_support_id: supportPath.id,
      requested_by_id: user.id,
      ...(assignedToId !== undefined ? { assigned_to_id: assignedToId } : {}),
      ...(notes !== undefined ? { notes } : {}),
    };

    if (assignment?.id) {
      await putItem('db/escalation-assignment', assignmentPayload);
      return;
    }

    const createPayload = { ...assignmentPayload };
    delete (createPayload as any).id;

    await postItem('db/escalation-assignment', createPayload);
  };

  const handleAssignMyself = async (escalationId) => {
    if (!user || !selectedQueue) return;

    const escalation = (myTeamEscalations ?? []).find(
      (item) => normalizeId(item.id) === normalizeId(escalationId)
    );

    const assignment = getLatestRelevantAssignment(escalation, selectedQueue);
    const supportPath = getEscalationSupportPathForQueue(escalation, selectedQueue);

    if (!escalation || !supportPath) return;

    await updateOrCreateAssignment({
      escalation,
      assignment,
      supportPath,
      assignedToId: user.id,
      statusId: ApprovalRequestStatusLevel.Completed,
      notes: `Self assign: ${user.email}`,
    });

    await invalidateQueueData();

    setSelectedEscalation(null);
  };

  const handleUnassignSelected = async () => {
    if (
      !selectedEscalationObject ||
      !selectedEscalationSupportPath
    ) {
      return;
    }

    await updateOrCreateAssignment({
      escalation: selectedEscalationObject,
      assignment: selectedEscalationAssignment,
      supportPath: selectedEscalationSupportPath,
      assignedToId: null,
      statusId: ApprovalRequestStatusLevel.Unassigned,
      notes: `Set unassigned: ${user?.email}`,
    });

    await invalidateQueueData();

    setUnassignModalOpen(false);
    setSelectedEscalation(null);
  };

  const assignButton = (escalationId): React.ReactNode => {
    return user?.isManager ? (
      <StbButton
        onClick={() => {
          setSelectedEscalation(escalationId);
          setAssignModalOpen(true);
        }}
      >
        Assign Specialist
      </StbButton>
    ) : (
      <StbButton
        onClick={() => {
          setSelectedEscalation(escalationId);
          handleAssignMyself(escalationId);
        }}
      >
        Assign Myself
      </StbButton>
    );
  };

  const assignedToMeActionButton = (escalationId): React.ReactNode => {
    if (user?.isManager) {
      return (
        <StbButton
          onClick={() => {
            setSelectedEscalation(escalationId);
            setAssignModalOpen(true);
          }}
        >
          Update Assignment
        </StbButton>
      );
    }

    return (
      <StbButton
        onClick={() => {
          setSelectedEscalation(escalationId);
          setUnassignModalOpen(true);
        }}
      >
        Set Unassigned
      </StbButton>
    );
  };

  const escalateToBuActionButton = (escalationId): React.ReactNode => {
    if (user?.isManager && !isAdmin) return null;
    
    return (
      <StbButton
        role="request"
        onClick={() => {
          setSelectedEscalation(escalationId);
          setEscalateModalOpen(true);
        }}
      >
        Escalate
      </StbButton>
    )
  };

  const assignedToOthersActionButton = (escalationId): React.ReactNode => {
    if (!user?.isManager) {
      return null;
    }

    return (
      <StbButton
        onClick={() => {
          setSelectedEscalation(escalationId);
          setAssignModalOpen(true);
        }}
      >
        Update Assignment
      </StbButton>
    );
  };

  const renderBody = (tableData, columns) => {
    if (!tableData || tableData.length === 0) {
      return null;
    }

    const list = [];

    tableData.forEach((row) => {
      columns.forEach((column) => {
        list.push(cellTemplate(column, row, row));
      });
    });

    return list;
  };

  const renderTable = ({
                         rows,
                         columns,
                         emptyMessage,
                       }: {
    rows: any[];
    columns: any[];
    emptyMessage?: string;
  }) => {
    const tableData = mapData(rows, opportunityData ?? [], selectedQueue);

    if (isInitialLoading) {
      return (
        <div
          className="support-path-table-shell"
          style={fullWidthShellStyle}
        >
          <TableLoadingSkeleton
            columnCount={columns.length}
            rowCount={5}
            width={tableWidth}
          />
        </div>
      );
    }

    return (
      <div
        className="support-path-table-shell"
        style={fullWidthShellStyle}
      >
        <HbrDomTable
          key={`${queueIdentity}:${currentTab}:${columns.length}:${tableData.length}`}
          autoLayout={true}
          columns={columns}
          data={tableData}
          density="spacious"
          style={{
            display: 'block',
            width: `${tableWidth}px`,
            maxWidth: `${tableWidth}px`,
            minWidth: `${tableWidth}px`,
          }}
        >
          <div slot="empty">
            <EmptyTableState message={emptyMessage} />
          </div>

          {renderBody(tableData, columns)}
        </HbrDomTable>
      </div>
    );
  };

  const renderLoadingPage = () => (
    <div
      className="p-4 support-path-page"
      style={pageShellStyle}
    >
      <HeaderLoadingSkeleton width={tableWidth} />
      <TableLoadingSkeleton
        columnCount={7}
        rowCount={5}
        width={tableWidth}
      />
    </div>
  );

  if (queuesLoading) {
    return renderLoadingPage();
  }

  if (!myQueues.length) {
    return (
      <div
        className="p-4 support-path-table-shell"
        style={fullWidthShellStyle}
      >
        <HbrDomTable
          key="no-queues"
          autoLayout={true}
          columns={supportPathTableView.columns}
          data={[]}
          density="spacious"
          style={{
            display: 'block',
            width: `${tableWidth}px`,
            maxWidth: `${tableWidth}px`,
            minWidth: `${tableWidth}px`,
          }}
        >
          <div slot="empty">
            <EmptyTableState message="You are not currently a member of any Domain Specialist or Incubation Team queues." />
          </div>
        </HbrDomTable>
      </div>
    );
  }

  if (queueKey && !selectedQueue) {
    return (
      <div
        className="p-4 support-path-table-shell"
        style={fullWidthShellStyle}
      >
        <HbrDomTable
          key={`unauthorized-${queueKey}`}
          autoLayout={true}
          columns={supportPathTableView.columns}
          data={[]}
          density="spacious"
          style={{
            display: 'block',
            width: `${tableWidth}px`,
            maxWidth: `${tableWidth}px`,
            minWidth: `${tableWidth}px`,
          }}
        >
          <div slot="empty">
            <EmptyTableState message="You do not have access to this support team queue." />
          </div>
        </HbrDomTable>
      </div>
    );
  }

  if (!selectedQueue) {
    return renderLoadingPage();
  }

  const baseColumns = [...supportPathTableView.columns];

  return (
    <>
      <div
        className="p-4 support-path-page"
        style={pageShellStyle}
      >
        <div
          className="mb-4"
          style={fullWidthShellStyle}
        >
          <div
            className="flex items-center gap-3"
            style={{
              width: '100%',
              minWidth: 0,
            }}
          >
            <div
              className="flex items-center gap-3"
              style={{
                minWidth: 0,
                flex: '1 1 auto',
              }}
            >
              <h1
                className="text-xl font-semibold"
                style={{
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {selectedQueue.title}
              </h1>

              {isSoftFetching && !isInitialLoading && (
                <span
                  className="hbr-css__skeleton hbr-css__skeleton-lg"
                  style={{
                    display: 'inline-block',
                    width: '80px',
                    flex: '0 0 auto',
                  }}
                  aria-label="refreshing queue data"
                />
              )}
            </div>

            <ProductTags productNames={selectedQueue.product_names} />
          </div>
        </div>

        {isDomainSpecialist && (
          <>
            {renderTable({
              rows: domainSpecialistAssigned,
              columns: baseColumns,
              emptyMessage: 'No assigned Domain Specialist opportunities to show.',
            })}
          </>
        )}

        {isIncubationTeam && (
          <HbrTabGroup
            className="mb-2"
            placement="top"
            style={{
              display: 'block',
              width: `${tableWidth}px`,
              maxWidth: `${tableWidth}px`,
              minWidth: 0,
              boxSizing: 'border-box',
            }}
          >
            <HbrTab
              slot="nav"
              onClick={() => handleTabClick('unassigned')}
              active={currentTab === 'unassigned'}
              panel="unassigned"
            >
              <div className="flex items-center gap-2">
                Unassigned
              </div>
            </HbrTab>

            <HbrTab
              slot="nav"
              onClick={() => handleTabClick('me')}
              active={currentTab === 'me'}
              panel="me"
            >
              <div className="flex items-center gap-2">
                Assigned to me
              </div>
            </HbrTab>

            <HbrTab
              slot="nav"
              onClick={() => handleTabClick('others')}
              active={currentTab === 'others'}
              panel="others"
            >
              <div className="flex items-center gap-2">
                Assigned to others
              </div>
            </HbrTab>

            <HbrTabPanel
              name="unassigned"
              className="py-4"
              style={fullWidthShellStyle}
            >
              {currentTab === 'unassigned' &&
                renderTable({
                  rows: unassigned,
                  columns: [
                    ...baseColumns,
                    {
                      id: 'actions',
                      name: '',
                      columnType: ColumnType.Custom,
                      renderCell: ({ data }) => assignButton(data.escalation),
                    },
                  ],
                  emptyMessage: 'No unassigned opportunities to show.',
                })}
            </HbrTabPanel>

            <HbrTabPanel
              name="me"
              className="py-4"
              style={fullWidthShellStyle}
            >
              {currentTab === 'me' &&
                renderTable({
                  rows: assignedToMe,
                  columns: [
                    ...baseColumns,
                    {
                      id: 'actions',
                      name: '',
                      columnType: ColumnType.Custom,
                      renderCell: ({ data }) => assignedToMeActionButton(data.escalation),
                    },
                    {
                      id: 'escalation',
                      name: '',
                      columnType: ColumnType.Custom,
                      renderCell: ({ data }) => escalateToBuActionButton(data.escalation),
                    }
                  ],
                  emptyMessage: 'No opportunities assigned to you.',
                })}

              <BuEscalationModal
                open={escalateModalOpen}
                onClose={() => setEscalateModalOpen(false)}
                escalation={selectedEscalationObject as EscalationRequest}
              />
            </HbrTabPanel>

            <HbrTabPanel
              name="others"
              className="py-4"
              style={fullWidthShellStyle}
            >
              {currentTab === 'others' &&
                renderTable({
                  rows: assignedToOthers,
                  columns: user?.isManager
                    ? [
                      ...baseColumns,
                      assignedToColumn,
                      {
                        id: 'actions',
                        name: '',
                        columnType: ColumnType.Custom,
                        renderCell: ({ data }) => assignedToOthersActionButton(data.escalation),
                      },
                    ]
                    : [
                      ...baseColumns,
                      assignedToColumn,
                    ],
                  emptyMessage: 'No opportunities assigned to others.',
                })}
            </HbrTabPanel>
          </HbrTabGroup>
        )}

        {assignModalOpen && selectedEscalationSupportPath && selectedEscalationObject && (
          <IncubationAssignmentModal
            escalation={selectedEscalationObject as EscalationRequest}
            supportPath={selectedEscalationSupportPath}
            currentAssignment={selectedEscalationAssignment}
            open={!!selectedEscalation}
            closeModal={async () => {
              setAssignModalOpen(false);
              setSelectedEscalation(null);
              await invalidateQueueData();
            }}
          />
        )}

        <StbModal
          title="Set Assignment to Unassigned"
          submitLabel="Set Unassigned"
          submitButtonRole="save"
          open={unassignModalOpen}
          onClose={async () => {
            setUnassignModalOpen(false);
            setSelectedEscalation(null);
          }}
          onSubmit={handleUnassignSelected}
        >
          <p>
            This will remove the current assignee and return the item to the
            unassigned queue.
          </p>
        </StbModal>
      </div>
    </>
  );
};

export default IncubationTeamOpportunityList;